import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, CheckCircle2, Circle, TrendingUp, Loader2, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { Progress } from "@/components/ui/progress";

export default function ReadinessReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/public/readiness/${token}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Report unavailable"); return r.json(); })
      .then(setData).catch((e) => setError(e.message));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="readiness-error">
      <ShieldCheck className="w-10 h-10 text-[#52525B] mb-4" />
      <p className="text-[#A1A1AA] max-w-sm">{error}</p>
      <Link to="/" className="text-[#D4AF37] hover:underline mt-4 text-sm">Go to Fairview Systems</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white" data-testid="readiness-report">
      <div className="max-w-3xl mx-auto px-5 py-10 sm:py-16">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg fv-gold-gradient flex items-center justify-center text-[#0A0A0B] font-heading font-bold">F</div>
          <div><div className="font-heading text-sm">Fairview</div><div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]">Systems</div></div>
          <span className="ml-auto text-[11px] text-[#52525B]">Business Readiness Report</span>
        </div>

        <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 sm:p-8 mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Business Readiness</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-white mb-4">{data.business_name}</h1>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="relative w-24 h-24 shrink-0">
              <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#D4AF37" strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={264} strokeDashoffset={264 - (264 * data.health_score) / 100} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="font-heading text-2xl">{data.health_score}</span><span className="text-[9px] text-[#71717A]">/100</span></div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[#D4AF37]"><TrendingUp className="w-4 h-4" /> Business Health Score</div>
              <div className="font-heading text-2xl mt-1">{data.health_band}</div>
              <div className="text-sm text-[#A1A1AA] mt-1">{data.milestones_done} of {data.milestones_total} milestones achieved · {data.overall}% overall completion</div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 mb-6">
          <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="readiness-factors">
            <h2 className="font-heading text-lg mb-3">Readiness breakdown</h2>
            <div className="space-y-2.5">
              {data.factors.map((f) => (
                <div key={f.label}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-[#D4D4D8]">{f.label}</span><span className="text-[#D4AF37]">{f.score}%</span></div>
                  <Progress value={f.score} className="h-1.5 bg-white/10" />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="readiness-milestones">
            <h2 className="font-heading text-lg mb-3">Milestones</h2>
            <div className="space-y-1.5">
              {data.milestones.map((m) => (
                <div key={m.label} className="flex items-center gap-2 text-sm">
                  {m.completed ? <CheckCircle2 className="w-4 h-4 text-[#D4AF37] shrink-0" /> : <Circle className="w-4 h-4 text-[#52525B] shrink-0" />}
                  <span className={m.completed ? "text-[#D4D4D8]" : "text-[#71717A]"}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 text-center">
          <p className="text-sm text-[#A1A1AA] mb-3">This report was generated by Fairview Systems — the business operating system for founders.</p>
          <Link to="/register" data-testid="readiness-cta" className="inline-flex items-center gap-1.5 fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-5 py-2.5 text-sm hover:opacity-90">
            Build your business readiness <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="text-center text-[11px] text-[#52525B] mt-6">Generated {new Date(data.generated_at).toLocaleDateString()} · Shared securely via Fairview Systems</p>
      </div>
    </div>
  );
}
